/**
 * LiveKit `RoomEvent` → the `MediaClient` event surface.
 *
 * The provider stays declarative: every event either re-reads the roster, moves
 * the speaker order, or re-asserts the current subscription plan. Nothing here
 * mutates the snapshot directly.
 */
import {
  DisconnectReason, RoomEvent,
  type ConnectionQuality as LiveKitQuality, type Participant, type RemoteTrack,
  type RemoteParticipant, type Room,
} from 'livekit-client';
import type { ConnectionQuality, MediaConnectionState } from './contract';
import type { AttachmentRegistry } from './livekitAttachments';
import type { AudioSink } from './livekitAudio';
import { toConnectionQuality } from './livekitRoster';
import type { MediaStore } from './mediaStore';

export interface RoomEventHost {
  /** Re-read participants from the room into the snapshot. */
  syncRoster(): void;
  setSpeakers(ids: readonly string[]): void;
  setState(state: MediaConnectionState): void;
  setLocalQuality(quality: ConnectionQuality): void;
  setRecording(recording: boolean): void;
  /** Re-assert the cached plan — publications are new objects after a resume. */
  reapplyPlan(): void;
  onAudioSubscribed(track: RemoteTrack): void;
  onAudioUnsubscribed(track: RemoteTrack): void;
  onVideoSubscribed(participantId: string, track: RemoteTrack): void;
  /**
   * The room ended, from this client's point of view. What that is worth is the
   * PROVIDER's decision, not this binding's: the reason code cannot separate a
   * hangup from a frozen tab, because `Room.disconnect()` reports
   * CLIENT_INITIATED whether a human clicked Leave or Chrome fired `freeze` and
   * livekit-client's own page-leave handler called it. Only the provider knows
   * whether IT asked to leave, so only the provider may conclude the call is
   * over. See `REJOIN_BACKOFF_MS` in livekitProvider.
   *
   * `removedByServer` is the ONE thing the reason code can say for certain, and
   * it is the exception to all of the above: PARTICIPANT_REMOVED and
   * ROOM_DELETED are decisions taken by the SFU on the server's instruction —
   * a moderator's Remove, "end meeting for everyone", or the API tearing down
   * the room of a call that has ended. No browser lifecycle event produces
   * either. Taking that seat back is not resilience, it is refusing to be
   * removed: LiveKit re-creates a deleted room for whoever joins it next, so
   * the rejoin loop put an evicted client straight back in, still publishing.
   */
  onDropped(removedByServer: boolean): void;
}

export interface RoomEventHostDeps {
  readonly store: MediaStore;
  readonly attachments: AttachmentRegistry;
  getSink(): AudioSink | null;
  syncRoster(): void;
  reapplyPlan(): void;
  setLocalQuality(quality: ConnectionQuality): void;
  onDropped(removedByServer: boolean): void;
}

/** The only place a LiveKit event is allowed to become snapshot state. */
export function createRoomEventHost(deps: RoomEventHostDeps): RoomEventHost {
  const { store } = deps;
  return {
    syncRoster: deps.syncRoster,
    reapplyPlan: deps.reapplyPlan,
    setLocalQuality: deps.setLocalQuality,
    setSpeakers: (activeSpeakerIds) => store.patch({ activeSpeakerIds }),
    setState: (state) => store.patch({ state }),
    setRecording: (recording) =>
      store.patch({
        recording: recording ? 'recording' : 'off',
        // Who started it is server knowledge; keep it once it is known.
        recordingStartedBy: recording
          ? store.get().recordingStartedBy ?? store.get().localId
          : null,
      }),
    onAudioSubscribed: (track) => deps.getSink()?.add(track),
    onAudioUnsubscribed: (track) => deps.getSink()?.remove(track),
    onVideoSubscribed: (participantId, track) => deps.attachments.flush(participantId, track),
    onDropped: deps.onDropped,
  };
}

/**
 * THE SERVER TOOK THE SEAT AWAY. Not a drop — a decision, and the only kind of
 * disconnect a client may not undo by itself.
 */
const isEviction = (reason: DisconnectReason | undefined): boolean =>
  reason === DisconnectReason.PARTICIPANT_REMOVED || reason === DisconnectReason.ROOM_DELETED;

/** A disconnect the user did not ask for; the UI must offer a re-join. */
const isFatal = (reason: DisconnectReason | undefined): boolean =>
  reason !== undefined &&
  reason !== DisconnectReason.CLIENT_INITIATED &&
  reason !== DisconnectReason.PARTICIPANT_REMOVED &&
  reason !== DisconnectReason.ROOM_DELETED;

export function bindRoomEvents(room: Room, host: RoomEventHost): () => void {
  const roster = (): void => host.syncRoster();

  const onSpeakers = (speakers: Participant[]): void => {
    host.setSpeakers(speakers.map((speaker) => speaker.identity));
    host.syncRoster();
  };

  const onQuality = (quality: LiveKitQuality, participant: Participant): void => {
    if (participant.isLocal) host.setLocalQuality(toConnectionQuality(quality));
    host.syncRoster();
  };

  const onSubscribed = (track: RemoteTrack, _pub: unknown, participant: RemoteParticipant): void => {
    host.onAudioSubscribed(track);
    host.onVideoSubscribed(participant.identity, track);
    host.syncRoster();
  };

  const onUnsubscribed = (track: RemoteTrack): void => {
    host.onAudioUnsubscribed(track);
    host.syncRoster();
  };

  const onPublished = (): void => {
    host.reapplyPlan();
    host.syncRoster();
  };

  const onReconnected = (): void => {
    host.setState('connected');
    host.reapplyPlan();
    host.syncRoster();
  };

  const onDisconnected = (reason?: DisconnectReason): void => {
    host.setState(isFatal(reason) ? 'failed' : 'disconnected');
    /* The provider unbinds these handlers BEFORE it disconnects on purpose, so
       reaching this line at all means the room went away on its own. */
    host.onDropped(isEviction(reason));
  };

  const onReconnecting = (): void => host.setState('reconnecting');
  const onRecording = (recording: boolean): void => host.setRecording(recording);

  room
    .on(RoomEvent.ParticipantConnected, onPublished)
    .on(RoomEvent.ParticipantDisconnected, onPublished)
    .on(RoomEvent.TrackPublished, onPublished)
    .on(RoomEvent.TrackUnpublished, onPublished)
    .on(RoomEvent.LocalTrackPublished, roster)
    .on(RoomEvent.LocalTrackUnpublished, roster)
    .on(RoomEvent.TrackSubscribed, onSubscribed)
    .on(RoomEvent.TrackUnsubscribed, onUnsubscribed)
    .on(RoomEvent.TrackMuted, roster)
    .on(RoomEvent.TrackUnmuted, roster)
    .on(RoomEvent.ParticipantAttributesChanged, roster)
    .on(RoomEvent.ParticipantMetadataChanged, roster)
    .on(RoomEvent.ParticipantNameChanged, roster)
    .on(RoomEvent.ActiveSpeakersChanged, onSpeakers)
    .on(RoomEvent.ConnectionQualityChanged, onQuality)
    .on(RoomEvent.RecordingStatusChanged, onRecording)
    .on(RoomEvent.Reconnecting, onReconnecting)
    .on(RoomEvent.SignalReconnecting, onReconnecting)
    .on(RoomEvent.Reconnected, onReconnected)
    .on(RoomEvent.Disconnected, onDisconnected);

  return () => {
    room
      .off(RoomEvent.ParticipantConnected, onPublished)
      .off(RoomEvent.ParticipantDisconnected, onPublished)
      .off(RoomEvent.TrackPublished, onPublished)
      .off(RoomEvent.TrackUnpublished, onPublished)
      .off(RoomEvent.LocalTrackPublished, roster)
      .off(RoomEvent.LocalTrackUnpublished, roster)
      .off(RoomEvent.TrackSubscribed, onSubscribed)
      .off(RoomEvent.TrackUnsubscribed, onUnsubscribed)
      .off(RoomEvent.TrackMuted, roster)
      .off(RoomEvent.TrackUnmuted, roster)
      .off(RoomEvent.ParticipantAttributesChanged, roster)
      .off(RoomEvent.ParticipantMetadataChanged, roster)
      .off(RoomEvent.ParticipantNameChanged, roster)
      .off(RoomEvent.ActiveSpeakersChanged, onSpeakers)
      .off(RoomEvent.ConnectionQualityChanged, onQuality)
      .off(RoomEvent.RecordingStatusChanged, onRecording)
      .off(RoomEvent.Reconnecting, onReconnecting)
      .off(RoomEvent.SignalReconnecting, onReconnecting)
      .off(RoomEvent.Reconnected, onReconnected)
      .off(RoomEvent.Disconnected, onDisconnected);
  };
}
